<template>
    <template v-if="current">
        <div class="mypage__bars">
            <div class="mypage__bar">
                <div class="mypage__bar-data"><span :style="{ top: (100 - current.targets.percentage_destinations) + '%' }"></span></div>
                <div class="mypage__bar-title">Turmål</div>
                <div class="mypage__bar-status">{{ current.targets.destinations }} av {{ current.targets.targets_destinations }}</div>
            </div>
            <div class="mypage__bar">
                <div class="mypage__bar-data"><span :style="{ top: (100 - current.targets.percentage_registrations) + '%' }"></span></div>
                <div class="mypage__bar-title">Registreringer</div>
                <div class="mypage__bar-status">{{ current.targets.registrations }} av {{ current.targets.targets_registrations }}</div>
            </div>
            <div class="mypage__bar">
                <div class="mypage__bar-data"><span :style="{ top: (100 - current.targets.percentage_distance) + '%' }"></span></div>
                <div class="mypage__bar-title">Kilometer</div>
                <div class="mypage__bar-status">{{ current.targets.distance }} av {{ current.targets.targets_distance }}</div>
            </div>
            <div class="mypage__bar">
                <div class="mypage__bar-data"><span :style="{ top: (100 - current.targets.percentage_elevation) + '%' }"></span></div>
                <div class="mypage__bar-title">Høydemeter</div>
                <div class="mypage__bar-status">{{ current.targets.elevation }} av {{ current.targets.targets_elevation }}</div>
            </div>
        </div>
        <div class="mypage__editbar">
            <a href="#" class="action" @click.prevent="openTargetsModal">Endre målsetting for din bruker</a>
        </div>
    </template>

    <div class="st-modal" :class="{ 'st-modal--active': showTargetsModal }">
        <div class="st-modal__content">
            <div class="st-modal__header">
                <div class="hero__header">Mine mål</div>
            </div>
            <div class="st-modal__text">
                Sett deg oppnåelige mål, så kan du heller justere det underveis i sesongen.
                <form @submit.prevent="submitTargets">
                    <h3>Antall turmål</h3>
                    <div class="st-modal__range">
                        <div class="st-modal__slider"><div class="st-slider" ref="destinationsSliderEl"></div></div>
                        <input type="text" class="st-modal__value" v-model.number="targetsForm.destinations" maxlength="3" @keypress="onlyDigits" @change="onFieldChange('destinations')">
                    </div>
                    <h3>Antall registreringer</h3>
                    <div class="st-modal__range">
                        <div class="st-modal__slider"><div class="st-slider" ref="registrationsSliderEl"></div></div>
                        <input type="text" class="st-modal__value" v-model.number="targetsForm.registrations" maxlength="3" @keypress="onlyDigits" @change="onFieldChange('registrations')">
                    </div>
                    <h3>Antall kilometer</h3>
                    <div class="st-modal__range">
                        <div class="st-modal__slider"><div class="st-slider" ref="distanceSliderEl"></div></div>
                        <input type="text" class="st-modal__value" v-model.number="targetsForm.distance" maxlength="5" @keypress="onlyDigits" @change="onFieldChange('distance')">
                    </div>
                    <h3>Antall høydemeter</h3>
                    <div class="st-modal__range">
                        <div class="st-modal__slider"><div class="st-slider" ref="elevationSliderEl"></div></div>
                        <input type="text" class="st-modal__value" v-model.number="targetsForm.elevation" maxlength="6" @keypress="onlyDigits" @change="onFieldChange('elevation')">
                    </div>
                    <div class="mypage__editbar">
                        <input type="submit" class="action" value="Oppdater">
                        <button type="button" class="action action--hollow" @click="showTargetsModal = false">Avbryt</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import noUiSlider from 'nouislider';

const props = defineProps({
    current: { type: Object, default: null },
    submitTargets: { type: Function, required: true },
});

// Sliders are created once and kept in sync with whichever member/
// competition is currently selected (min/max/value differ per person, not
// just per field) - see syncSlidersToTargets(), called from openTargetsModal().
const showTargetsModal = ref(false);
const targetsForm = reactive({ destinations: 0, registrations: 0, distance: 0, elevation: 0 });
const destinationsSliderEl = ref(null);
const registrationsSliderEl = ref(null);
const distanceSliderEl = ref(null);
const elevationSliderEl = ref(null);

const sliderSpecs = [
    { key: 'destinations', el: destinationsSliderEl, step: 1 },
    { key: 'registrations', el: registrationsSliderEl, step: 1 },
    { key: 'distance', el: distanceSliderEl, step: 5 },
    { key: 'elevation', el: elevationSliderEl, step: 5 },
];
const sliderInstances = {};

onMounted(() => {
    sliderSpecs.forEach(({ key, el, step }) => {
        noUiSlider.create(el.value, {
            start: 0,
            connect: [true, false],
            step,
            range: { min: 1, max: 100 },
        });
        sliderInstances[key] = el.value.noUiSlider;
        sliderInstances[key].on('update', (values, handle) => {
            targetsForm[key] = Math.round(values[handle]);
        });
    });
});

function onlyDigits(e) {
    const key = e.key || String.fromCharCode(e.which);
    if (!/[0-9]/.test(key)) {
        e.preventDefault();
    }
}

function onFieldChange(key) {
    const slider = sliderInstances[key];
    const value = targetsForm[key];
    const options = slider.options;
    if (value > options.range.max) {
        slider.updateOptions({ range: { min: options.range.min, max: value } }, false);
    }
    slider.set(value);
}

function syncSlidersToTargets(targets) {
    sliderSpecs.forEach(({ key }) => {
        const min = targets[key + '_min'];
        const max = targets[key + '_max'];
        const value = targets['targets_' + key];
        targetsForm[key] = value;
        sliderInstances[key].updateOptions({ range: { min, max } }, false);
        sliderInstances[key].set(value);
    });
}

function openTargetsModal() {
    if (props.current) {
        syncSlidersToTargets(props.current.targets);
    }
    showTargetsModal.value = true;
}

async function submitTargets() {
    await props.submitTargets({ ...targetsForm });
    showTargetsModal.value = false;
}
</script>
